/**
 * The Event Hub loading overlay — one mask over the whole content column, instead of each
 * section showing its own spinner, dash or dimmed grid while the page's first data arrives.
 *
 * A page says it is loading with {@link useEventHubLoading}; EventHubLayout owns the single
 * overlay and shows it while ANY page or section is. Counted rather than a boolean so two
 * sections loading at once (the home page fetches events and nominations separately) do not
 * hide it when the first one finishes.
 *
 * The overlay covers the content column only — not the portal chrome or the chat panel, which
 * stay usable while Event Hub loads.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Spinner } from '@amzn/awsui-components-react';

interface LoadingRegistry {
  begin: () => void;
  end: () => void;
}

const EventHubLoadingContext = createContext<LoadingRegistry | null>(null);

export function EventHubLoadingProvider({
  children,
}: {
  children: (loading: boolean) => ReactNode;
}) {
  const [count, setCount] = useState(0);
  const begin = useCallback(() => setCount((c) => c + 1), []);
  const end = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);
  const registry = useMemo(() => ({ begin, end }), [begin, end]);
  return (
    <EventHubLoadingContext.Provider value={registry}>
      {children(count > 0)}
    </EventHubLoadingContext.Provider>
  );
}

/**
 * Shows the overlay while `active` is true. Outside EventHubLayout (a page rendered on its own,
 * as in its tests) it does nothing.
 */
export function useEventHubLoading(active: boolean): void {
  const registry = useContext(EventHubLoadingContext);
  useEffect(() => {
    if (!active || !registry) return;
    registry.begin();
    return registry.end;
  }, [active, registry]);
}

export function EventHubLoadingOverlay({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="event-hub-layout__overlay" role="status" aria-live="polite">
      <Spinner size="large" />
      <span className="event-hub-layout__overlay-label">Loading…</span>
    </div>
  );
}
